import { useEffect, useState } from "react";
import { Loader2, CalendarCheck, CalendarRange, ListChecks, Download, FileSpreadsheet } from "lucide-react";
import { toast } from "@/components/ui/sonner";
import api from "@/lib/api";
import { brl, pct } from "@/lib/format";

function ExportBar() {
  const [periodo, setPeriodo] = useState("mes");
  const [status, setStatus] = useState("todos");
  const [busy, setBusy] = useState(null);

  const baixar = async (formato) => {
    setBusy(formato);
    try {
      const r = await api.get("/admin/relatorio/export", { params: { formato, periodo, status }, responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `contribuicoes-${new Date().toISOString().slice(0, 10)}.${formato}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Planilha gerada!");
    } catch (e) {
      toast.error("Não foi possível gerar a planilha.");
    } finally {
      setBusy(null);
    }
  };

  const sel = "h-11 rounded-xl border border-slate-200 bg-white px-3 text-sm font-semibold text-slate-700";
  return (
    <div data-testid="report-export" className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center gap-3">
      <div className="flex items-center gap-3 flex-1">
        <div className="w-10 h-10 rounded-xl bg-emerald-600 flex items-center justify-center shrink-0"><FileSpreadsheet className="w-5 h-5 text-white" /></div>
        <div>
          <p className="font-heading text-base font-bold text-slate-900">Exportar para prestação de contas</p>
          <p className="text-xs text-slate-500">Planilha com todas as contribuições e resumo por meta.</p>
        </div>
      </div>
      <div className="flex flex-wrap gap-2">
        <select data-testid="export-periodo" value={periodo} onChange={(e) => setPeriodo(e.target.value)} className={sel}>
          <option value="hoje">Hoje</option>
          <option value="mes">Este mês</option>
          <option value="tudo">Tudo</option>
        </select>
        <select data-testid="export-status" value={status} onChange={(e) => setStatus(e.target.value)} className={sel}>
          <option value="todos">Todos os status</option>
          <option value="confirmado">Só confirmados</option>
          <option value="pendente">Só pendentes</option>
          <option value="recusado">Só recusados</option>
        </select>
        <button data-testid="export-xlsx" disabled={!!busy} onClick={() => baixar("xlsx")} className="inline-flex items-center gap-2 h-11 px-4 rounded-xl bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white font-bold text-sm transition-colors">
          {busy === "xlsx" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />} Excel
        </button>
        <button data-testid="export-csv" disabled={!!busy} onClick={() => baixar("csv")} className="inline-flex items-center gap-2 h-11 px-4 rounded-xl bg-slate-100 hover:bg-slate-200 disabled:opacity-60 text-slate-700 font-bold text-sm border border-slate-200 transition-colors">
          {busy === "csv" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />} CSV
        </button>
      </div>
    </div>
  );
}

export default function ReportPanel() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/admin/relatorio").then((r) => setData(r.data)).catch(() => setData({ erro: true }));
  }, []);

  if (!data) return <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-blue-800" /></div>;
  if (data.erro) return <div className="bg-white rounded-2xl border p-8 text-center text-slate-500">Não foi possível carregar o relatório.</div>;

  return (
    <div data-testid="report-panel" className="space-y-6">
      <ExportBar />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Block icon={CalendarCheck} title="HOJE" testid="report-hoje">
          <Row label="Contribuições recebidas" value={data.hoje.contribuicoes} testid="report-hoje-contribuicoes" />
          <Row label="Total confirmado hoje" value={brl(data.hoje.total_confirmado)} strong="text-emerald-700" testid="report-hoje-confirmado" />
          <Row label="Aguardando confirmação" value={`${data.hoje.pendentes} · ${brl(data.hoje.total_pendente)}`} strong="text-amber-700" testid="report-hoje-pendentes" />
        </Block>
        <Block icon={CalendarRange} title="ESTE MÊS" testid="report-mes">
          <Row label="Total arrecadado (confirmado)" value={brl(data.mes.total_arrecadado)} strong="text-emerald-700" testid="report-mes-total" />
          <Row label="Contribuições confirmadas" value={data.mes.contribuicoes} testid="report-mes-contribuicoes" />
        </Block>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden" data-testid="report-por-meta">
        <div className="flex items-center gap-3 p-5 border-b border-slate-100">
          <div className="w-10 h-10 rounded-xl bg-blue-800 flex items-center justify-center"><ListChecks className="w-5 h-5 text-white" /></div>
          <h3 className="font-heading text-lg font-bold text-slate-900">POR META</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wide">
              <tr>
                <th className="text-left px-5 py-3 font-semibold">Meta</th>
                <th className="text-right px-4 py-3 font-semibold">Total</th>
                <th className="text-right px-4 py-3 font-semibold">Arrecadado</th>
                <th className="text-right px-4 py-3 font-semibold">Restante</th>
                <th className="text-right px-4 py-3 font-semibold">Contribuintes</th>
              </tr>
            </thead>
            <tbody>
              {data.por_meta.length === 0 && (
                <tr><td colSpan={5} className="px-5 py-8 text-center text-slate-500">Nenhuma meta cadastrada.</td></tr>
              )}
              {data.por_meta.map((m) => (
                <tr key={m.id} data-testid={`report-meta-row-${m.id}`} className="border-t border-slate-100">
                  <td className="px-5 py-3">
                    <p className="font-semibold text-slate-900">{m.referente}</p>
                    <p className="text-xs text-slate-500">{m.status === "pago" ? "✓ Concluída" : `${pct(m.percentual)}% · ${m.ativo ? "publicada" : "não publicada"}`}{m.pendentes ? ` · ${m.pendentes} pendente(s)` : ""}</p>
                  </td>
                  <td className="px-4 py-3 text-right font-semibold text-slate-800 whitespace-nowrap">{brl(m.valor_total)}</td>
                  <td className="px-4 py-3 text-right font-semibold text-emerald-700 whitespace-nowrap">{brl(m.valor_arrecadado)}</td>
                  <td className="px-4 py-3 text-right font-semibold text-blue-900 whitespace-nowrap">{brl(m.valor_restante)}</td>
                  <td className="px-4 py-3 text-right font-semibold text-slate-800">{m.contribuintes}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function Block({ icon: Icon, title, children, testid }) {
  return (
    <div data-testid={testid} className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-xl bg-blue-800 flex items-center justify-center"><Icon className="w-5 h-5 text-white" /></div>
        <h3 className="font-heading text-lg font-bold text-slate-900">{title}</h3>
      </div>
      <div className="divide-y divide-slate-100">{children}</div>
    </div>
  );
}

function Row({ label, value, strong = "text-slate-900", testid }) {
  return (
    <div className="flex items-center justify-between py-2.5 gap-3">
      <span className="text-slate-600">{label}</span>
      <span data-testid={testid} className={`font-heading font-extrabold text-lg ${strong}`}>{value}</span>
    </div>
  );
}
